import React, { Fragment, useCallback, useMemo } from "react";

import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Box, Typography } from "@mui/material";

import { useTeamsFiles } from "@/hooks/useTeamsFiles";
import { buildTreeFromSelectedFiles, TreeNodeWithChildren } from "@/utils/treeBuilder";

import styles from "./TeamsIntegrationReviewStep.module.scss";

// Internal tree node type for review
type ReviewTreeNode = TreeNodeWithChildren<ReviewTreeNode> & {
  type: "folder" | "file";
};

interface ITeamsIntegrationReviewStepProps {}

export const TeamsIntegrationReviewStep: React.FC<ITeamsIntegrationReviewStepProps> = () => {
  const { getSelectedFiles, getTeamsFilesByParentId, getTeamsFileById } = useTeamsFiles();

  // Build internal tree for review using the utility
  const reviewTreeNodes = useMemo(() => {
    const selectedFiles = getSelectedFiles();

    return buildTreeFromSelectedFiles<ReviewTreeNode>(
      selectedFiles,
      getTeamsFilesByParentId,
      getTeamsFileById,
      (file, level) => ({
        id: file.id,
        name: file.name,
        folder: !!file.folder,
        category: file.category || null,
        type: file.folder ? "folder" : "file",
        level,
      })
    );
  }, [getSelectedFiles, getTeamsFilesByParentId, getTeamsFileById]);
  const renderTreeLevel = useCallback((items: ReviewTreeNode[], level: number = 0) => {
    if (!items || items.length === 0) return null;

    return items.map((item) => {
      const { type, id, name } = item;

      return (
        <Box key={id} display={"flex"} flexDirection="column">
          <Box display={"flex"} alignItems="center" height="27px">
            <Box width={`${level > 1 ? 29 * level : 0}px`} />
            <Box display={"flex"} alignItems="center" gap={1}>
              {level !== 0 && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SubdirectoryArrowRightIcon sx={{ color: "grey.500" }} />
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {type === "folder" && <FolderOpenOutlinedIcon />}
                {type === "file" && <InsertDriveFileOutlinedIcon />}
              </Box>
              <Typography>{name}</Typography>
            </Box>
          </Box>
          {renderTreeLevel(item.children, level + 1)}
        </Box>
      );
    });
  }, []);

  const renderCategory = useCallback((items: ReviewTreeNode[]) => {
    if (!items || items.length === 0) return null;

    return items.map((item) => {
      const { id, category } = item;
      return (
        <Box key={id} display={"flex"} flexDirection="column">
          <Box
            sx={{ display: "flex", alignItems: "center", height: "27px", wrap: "nowrap" }}
            gap={1}
          >
            {category?.map((value, index) => (
              <Fragment key={value}>
                <Typography>{value}</Typography>
                {index !== category.length - 1 && <Box className={styles.divider} />}
              </Fragment>
            ))}
          </Box>

          {renderCategory(item.children)}
        </Box>
      );
    });
  }, []);

  return (
    <Box className={styles.container}>
      <Box className={styles.treeView}>
        <Typography className={styles.title}>Selected files</Typography>
        {renderTreeLevel(reviewTreeNodes)}
      </Box>
      <Box className={styles.treeView}>
        <Typography className={styles.title}>Category</Typography>
        {renderCategory(reviewTreeNodes)}
      </Box>
    </Box>
  );
};
